import React, { useRef, useCallback, useEffect } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import { List, CellMeasurer, CellMeasurerCache } from 'react-virtualized'
import { CheckCircle, AlertCircle, ConstructionIcon } from 'lucide-react'

const SkeletonRow = () => (
  <div className='flex border-b border-gray-200 p-2 animate-pulse'>
    <div className='flex-1 px-2 py-1 bg-gray-200 rounded-md mr-2' style={{ height: 30 }} />
  </div>
)

const LoadingMoreRow = () => (
  <div className='flex justify-center items-center p-2 text-gray-500'>
    Loading more...
  </div>
)

const NoDataRow = ({ hasSelection }) => (
  <div className='px-2 py-4 text-center text-gray-500'>
    {hasSelection
      ? 'No caregiver details available for this record'
      : 'Select a record to view caregiver details'}
  </div>
)

const MeasuredRow = React.forwardRef(({ children, style }, ref) => (
  <div ref={ref} style={style}>
    {children}
  </div>
))

const CaregiverDetailsTable = ({
  selectedServiceRecord,
  caregiverDetails,
  isInitialLoading,
  isLoadingMore,
  fetchNextBatch,
  hasMore
}) => {
  const cache = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      minHeight: 50
    })
  )
  const listRef = useRef()

  useEffect(() => {
    if (listRef.current) {
      cache.current.clearAll()
      listRef.current.recomputeRowHeights()
    }
  }, [caregiverDetails, selectedServiceRecord])


  useEffect(() => {
    fetchNextBatch()
  }, [selectedServiceRecord])


  const Row = ({ index, key, parent, style }) => (
    <CellMeasurer
      key={key}
      cache={cache.current}
      parent={parent}
      columnIndex={0}
      rowIndex={index}
    >
      {({ registerChild }) => {
        if (isInitialLoading) {
          return (
            <MeasuredRow ref={registerChild} style={style}>
              <SkeletonRow />
            </MeasuredRow>
          )
        }

        if (caregiverDetails.length === 0) {
          return (
            <MeasuredRow ref={registerChild} style={style}>
              <NoDataRow hasSelection={!!selectedServiceRecord} />
            </MeasuredRow>
          )
        }

        if (index >= caregiverDetails.length) {
          return (
            <MeasuredRow ref={registerChild} style={style}>
              <LoadingMoreRow />
            </MeasuredRow>
          )
        }

        const detail = caregiverDetails[index]

        return (
          <MeasuredRow ref={registerChild} style={style}>
            <div className='flex border-b border-gray-200 p-2 bg-white hover:bg-indigo-50 transition-colors'>
              <div className='flex-1 px-2 py-1 text-sm text-gray-900'>
                {detail.OFFICE_STATE_CODE || '-'}
              </div>
              <div className='flex-1 px-2 py-1 text-sm text-gray-900'>
                {detail.NEW_PARENT_BRANCH_SHORTENED || '-'}
              </div>
              <div className='flex-1 px-2 py-1 text-sm text-gray-900'>
                {detail.CLIENT_NAME || '-'}
              </div>
              <div className='flex-1 px-2 py-1 text-sm text-gray-900'>
                {detail.CAREGIVER_NAME || '-'}
              </div>
              <div className='flex-1 px-2 py-1 text-sm font-medium text-gray-900'>
                {detail.TOTAL_HOURS_SERVED ?? '-'}
              </div>
              <div className='flex-1 px-2 py-1 text-sm text-gray-900'>
                {detail.LATEST_SERVICE_DATE || '-'}
              </div>
              <div className='flex-1 px-2 py-1'>
                {detail.CAREGIVER_TYPE_ICON ? (
                  <div className='flex items-center space-x-1'>
                    <CheckCircle className='w-4 h-4 text-green-500' />
                    <span className='text-xs text-green-700 font-medium'>PREFERRED</span>
                  </div>
                ) : (
                  <span className='text-xs text-gray-500'>-</span>
                )}
              </div>
            </div>
          </MeasuredRow>
        )
      }}
    </CellMeasurer>
  )

  const handleScroll = useCallback(
    ({ clientHeight, scrollHeight, scrollTop }) => {
      if (
        scrollHeight - scrollTop - clientHeight < 300 &&
        hasMore &&
        !isLoadingMore &&
        !isInitialLoading
      ) {
        fetchNextBatch()
      }
    },
    [fetchNextBatch, hasMore, isLoadingMore, isInitialLoading]
  )

  const rowCount = isInitialLoading
    ? 10
    : caregiverDetails.length > 0
    ? caregiverDetails.length + (isLoadingMore ? 1 : 0)
    : 1

  return (
    <div className='container mx-auto bg-white rounded-xl shadow border border-gray-200 overflow-hidden mb-5'>
      <div className='px-6 py-4 border-b border-gray-200 bg-gray-50'>
        <h2 className='text-lg font-semibold text-gray-900'>Caregiver Details</h2>
        {selectedServiceRecord && (
          <p className='text-sm text-gray-600 mt-1'>
            {selectedServiceRecord.CSS_NAME && <>CSS: <b>{selectedServiceRecord.CSS_NAME}</b></>}
            {selectedServiceRecord.CSS_NAME && selectedServiceRecord.CONTRACT_NAME && ' | '}
            {selectedServiceRecord.CONTRACT_NAME && <>Contract: <b>{selectedServiceRecord.CONTRACT_NAME}</b></>}
          </p>
        )}
      </div>

      <div className='flex bg-purple-700 text-white font-medium text-xs uppercase sticky top-0 z-10'>
        <div className='flex-1 px-2 py-2'>STATE</div>
        <div className='flex-1 px-2 py-2'>BRANCH</div>
        <div className='flex-1 px-2 py-2'>CLIENT</div>
        <div className='flex-1 px-2 py-2'>CAREGIVER</div>
        <div className='flex-1 px-2 py-2'>HOURS SERVED</div>
        <div className='flex-1 px-2 py-2'>LAST SERVICE DATE</div>
        <div className='flex-1 px-2 py-2'>STATUS</div>
      </div>

      <div style={{ height: 300 }}>
        <AutoSizer>
          {({ width, height }) => (
            <List
              key={selectedServiceRecord?.CLIENT_NUMBER || 'default'}
              ref={listRef}
              width={width}
              height={height}
              rowCount={rowCount}
              rowHeight={cache.current.rowHeight}
              deferredMeasurementCache={cache.current}
              rowRenderer={Row}
              overscanRowCount={5}
              onScroll={handleScroll}
            />
          )}
        </AutoSizer>
      </div>

      <div className='px-6 py-3 bg-blue-50 border-t border-gray-200'>
        <p className='text-xs text-blue-800'>
          <span className='font-medium'>NOTE:</span>
          <CheckCircle className='w-3 h-3 inline mx-1 text-green-500' />
          Identifies Caregivers who are 'PREFERRED/FAMILY STRUCTURE'.
          <AlertCircle className='w-3 h-3 inline mx-1 text-red-500' />
          Identifies Clients never served with active authorization.
        </p>
      </div>
    </div>
  )
}

export default CaregiverDetailsTable
